/**
 * Talking to the admin endpoints.
 *
 * A plain module with no rune in it, for the same reason `account-io.ts` is
 * one: `$state` only exists once the Svelte compiler has been over a file, so
 * anything that has to run under plain Vitest lives here and the reactive
 * wrapper lives in `admin.svelte.ts`.
 *
 * It reuses `account-io.ts`'s `request` rather than repeating it. That is not
 * only economy: `credentials: 'same-origin'` — the line that actually carries
 * the session cookie — and the rule that no answer from the server is ever an
 * exception are decisions this file must not be able to make differently.
 *
 * The one outcome that matters here and nowhere else is **`forbidden`**. A
 * signed-in player who is not an administrator gets 403 from every route, and
 * the panel answers that with a screen rather than with a message in the
 * corner — see `screens/AdminScreen.svelte`.
 *
 * ## Nothing here is a permission check
 *
 * There is no `isAdmin` guard in this module, and the panel's entry point
 * reading `me.isAdmin` is a courtesy to the person, not a gate. The gate is
 * `apps/server/src/http/admin-api.ts`, which resolves the session server-side
 * on every route. Everything in this file would still be refused if it were
 * called from a console.
 */
import { request } from './account-io.js';
import type { AccountBest, AccountOutcome, AccountPlayer, Fetcher } from './account-io.js';

export const ADMIN_OVERVIEW = '/api/admin/overview';
export const ADMIN_PLAYERS = '/api/admin/players';

export interface AdminOverview {
  readonly players: number;
  readonly matches: number;
  readonly matchesToday: number;
  readonly liveRooms: number;
  readonly admins: number;
  readonly accountsEnabled: boolean;
}

export interface AdminRecord {
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

export interface AdminPlayerSummary {
  readonly id: string;
  readonly username: string;
  readonly createdAtMs: number;
  readonly bestCount: number;
  readonly record: AdminRecord;
  /** See the server's `model.ts#PlayerSummary`; `null` means never. */
  readonly lastSeenAtMs: number | null;
}

export interface AdminPlayerPage {
  readonly total: number;
  readonly pageSize: number;
  readonly players: readonly AdminPlayerSummary[];
}

export interface AdminMatch {
  readonly matchId: string;
  readonly roomCode: string;
  readonly matchType: string;
  readonly variant: string;
  readonly startedAtMs: number;
  readonly endedAtMs: number;
  readonly endReason: string;
  readonly outcome: 'win' | 'loss' | 'draw';
  readonly roundsWon: number;
  readonly opponents: readonly string[];
}

export interface AdminPlayerDetail {
  readonly player: AccountPlayer;
  readonly summary: AdminPlayerSummary;
  readonly bests: readonly AccountBest[];
  readonly matches: readonly AdminMatch[];
  readonly isAdmin: boolean;
}

export interface AdminTemporaryPassword {
  readonly id: string;
  /** Shown once and never fetched again. There is no route that re-reads it. */
  readonly password: string;
  readonly sessionsRevoked: number;
}

export function fetchOverview(fetcher?: Fetcher): Promise<AccountOutcome<AdminOverview>> {
  return request(ADMIN_OVERVIEW, { method: 'GET', ...maybeFetcher(fetcher) });
}

/**
 * One page of the player list.
 *
 * The query is encoded rather than interpolated — a username can hold `-` and
 * `_` and a search box can hold anything at all, and a `&` typed into it must
 * not become a second parameter.
 */
export function fetchPlayers(
  query: string,
  page: number,
  fetcher?: Fetcher,
): Promise<AccountOutcome<AdminPlayerPage>> {
  const search = new URLSearchParams({ query, page: String(page) });
  return request(`${ADMIN_PLAYERS}?${search.toString()}`, {
    method: 'GET',
    ...maybeFetcher(fetcher),
  });
}

export function fetchPlayer(
  id: string,
  fetcher?: Fetcher,
): Promise<AccountOutcome<AdminPlayerDetail>> {
  return request(playerPath(id), { method: 'GET', ...maybeFetcher(fetcher) });
}

export function deletePlayer(
  id: string,
  fetcher?: Fetcher,
): Promise<AccountOutcome<{ deleted: boolean; id: string }>> {
  return request(playerPath(id), { method: 'DELETE', ...maybeFetcher(fetcher) });
}

export function resetPassword(
  id: string,
  fetcher?: Fetcher,
): Promise<AccountOutcome<AdminTemporaryPassword>> {
  return request(`${playerPath(id)}/password`, { method: 'POST', ...maybeFetcher(fetcher) });
}

/** An id is a path segment, and a path segment is encoded. */
function playerPath(id: string): string {
  return `${ADMIN_PLAYERS}/${encodeURIComponent(id)}`;
}

function maybeFetcher(fetcher: Fetcher | undefined): { fetcher?: Fetcher } {
  return fetcher === undefined ? {} : { fetcher };
}
